import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  FormHelperText,
  makeStyles,
} from "@material-ui/core";
import { Instagram, LinkedIn, Lock, Twitter, YouTube } from "@material-ui/icons";
import { useTranslation } from "react-i18next";

import * as DataNet from "../components/DataNet/DataNet";
import * as ROUTES from "../constants/routes";
import BexenCardioLogo from "../assets/images/0_3_BEXEN_Cardio_Horiz.png";
import login_img from "../assets/images/login.jpg";

const normalizeLanguage = (lang) => {
  const value = (lang || "es").toString().toLowerCase();

  if (value.startsWith("es")) return "es";
  if (value.startsWith("fr")) return "fr";
  if (value.startsWith("en")) return "en";
  if (value.startsWith("it")) return "it";

  return "es";
};

const clearA2fSession = () => {
  sessionStorage.removeItem("a2fInProgress");
  sessionStorage.removeItem("a2fChallengeId");
  sessionStorage.removeItem("a2fExpiresInSeconds");
  sessionStorage.removeItem("a2fRedirectTo");
  sessionStorage.removeItem("a2fDefaultLang");
};

const TwoFactorVerifyPage = (props) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const challengeId =
    props.location?.state?.challengeId ||
    sessionStorage.getItem("a2fChallengeId");

  const redirectTo =
    props.location?.state?.redirectTo ||
    sessionStorage.getItem("a2fRedirectTo") ||
    ROUTES.SIGN_IN;

  const defaultLang = normalizeLanguage(
    props.location?.state?.defaultLang ||
      sessionStorage.getItem("a2fDefaultLang")
  );

  useEffect(() => {
    i18n.changeLanguage(defaultLang);
  }, [defaultLang, i18n]);

  const handleCodeChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
    setError(null);
  };

  const handleBackToLogin = () => {
    clearA2fSession();
    props.history.push(ROUTES.SIGN_IN);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (loading) return;

    if (!challengeId) {
      handleBackToLogin();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await DataNet.verifyA2fLogin({
        challengeId,
        code,
      });

      if (
        response?.status &&
        response.status.mfaRequired === false &&
        (response.status.isLogged === true ||
          response.status.islogged === true)
      ) {
        clearA2fSession();
        window.location.href = redirectTo || ROUTES.SIGN_IN;
        return;
      }

      setError(t("a2f.error.invalidOrExpired"));
    } catch (err) {
      setError(t("a2f.error.verifyError"));
    } finally {
      setLoading(false);
    }
  };

  const isInvalid = loading || code.length !== 6;

  const renderA2fForm = () => (
    <form onSubmit={handleSubmit}>
      <Grid container justify="center" alignItems="center" spacing={4}>
        <Grid item xs={12} justify="center" className={classes.title}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            color="secondary"
            style={{
              marginBottom: 0,
              fontWeight: 500,
              fontSize: "2.3rem",
            }}
          >
            REANIBEX DATA NET
          </Typography>

          <Grid container direction="row" alignContent="flex-end">
            <Grid item>
              <Typography
                variant="h6"
                gutterBottom
                color="secondary"
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 5,
                  marginRight: 4,
                }}
              >
                by
              </Typography>
            </Grid>

            <Grid item>
              <img
                src={BexenCardioLogo}
                alt="Bexen Cardio"
                style={{ width: 148 }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={12}
          sm={12}
          md={9}
          xl={9}
          spacing={2}
          justify="center"
        >
          <Grid item xs={12} sm={7} md={12} xl={12}>
            <Typography
              variant="h6"
              color="secondary"
              style={{
                fontSize: 18,
                fontWeight: 600,
                marginBottom: 4,
                textAlign: "center",
              }}
            >
              {t("a2f.title")}
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              style={{
                marginBottom: 8,
                textAlign: "center",
              }}
            >
              {t("a2f.instructions")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={7} md={12} xl={12}>
            <TextField
              id="a2f-code"
              variant="outlined"
              name="code"
              error={!!error}
              value={code}
              onChange={handleCodeChange}
              type="text"
              label={t("a2f.code")}
              fullWidth
              autoFocus
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            <Grid item xs={12} sm={7} md={12} xl={12}>
              <FormHelperText error>{error}</FormHelperText>
            </Grid>
          </Grid>
        </Grid>

        <Grid container item xs={12} md={9} spacing={2} justify="center">
          <Grid item xs={12} sm={6} md={12} container justify="center">
            {loading ? (
              <CircularProgress />
            ) : (
              <Button
                size="medium"
                fullWidth
                variant="contained"
                disabled={isInvalid}
                style={{ textTransform: "none" }}
                color="secondary"
                type="submit"
              >
                {t("a2f.verify")}
              </Button>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={12}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              fullWidth
              style={{ textTransform: "none" }}
              onClick={handleBackToLogin}
            >
              {t("a2f.backToLogin")}
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={8} style={{ width: "100%" }}>
          <Divider />
        </Grid>

        <Grid
          container
          item
          xs={12}
          direction="row"
          justify="center"
          alignItems="center"
          style={{ width: "100%" }}
        >
          <Grid item container xs={3} justify="center" alignItems="center">
              <IconButton color="secondary" onClick={() => window.open("https://www.linkedin.com/company/bexen-cardio/", "_blank")}>
                  <LinkedIn fontSize="large" />
              </IconButton>
          </Grid>

          <Grid item container xs={3} justify="center" alignItems="center">
              <IconButton color="secondary" onClick={() => window.open("https://www.youtube.com/channel/UCqkQ7fCHKFMVOc7jv5xffqw", "_blank")}>
                  <YouTube fontSize="large" />
              </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );

  return (
    <>
      <Hidden xsDown>
        <Grid
          container
          justify="center"
          alignItems="center"
          style={{ minHeight: "80vh", marginTop: 0 }}
        >
          <Grid container item lg={7} xs={12} xl={6} md={6}>
            <Paper
              variant="outlined"
              style={{
                width: "100%",
                borderRadius: 16,
                backgroundImage:
                  "linear-gradient(0deg,rgba(255,255,255,.5),rgba(255,255,255,.5)),url()",
              }}
            >
            <Grid
            container
            style={{
                borderTopLeftRadius: 16,
                borderBottomLeftRadius: 16,
                minHeight: 570,
                paddingTop: "1.5rem"
            }}
            >
                <Hidden smDown>
                  <Grid
                    container
                    item
                    alignItems="center"
                    justify="center"
                    sm={6}
                    style={{
                      borderTopLeftRadius: 16,
                      borderBottomLeftRadius: 16,
                      backgroundPosition: "-95px",
                      backgroundSize: "cover",
                      backgroundImage:
                        "linear-gradient(0deg,rgba(255,255,255,.15),rgba(255,255,255,.15)), " +
                        `url(${login_img})`,
                    }}
                  />
                </Hidden>

                <Grid
                item
                justify="center"
                alignItems="center"
                sm={12}
                lg={6}
                container
                style={{ padding: 32 }}
                >
                    {renderA2fForm()}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Hidden>

      <Hidden smUp>
        <Grid container alignItems="center" style={{ minHeight: "90vh" }}>
          {renderA2fForm()}
        </Grid>
      </Hidden>
    </>
  );
};

export default TwoFactorVerifyPage;

const useStyles = makeStyles((theme) => ({
  title: {
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(2),
    },
  },
}));